import type { TFunction } from 'i18next';
import type { AlertSubscription } from '@/lib/alerts-schemas';
import { SWISS_CANTON_NAMES } from '@/lib/company-utils';
import { getCategoryLabel } from '@/lib/industry-categories';
import { RECHTSFORM_OPTIONS } from '@/new_components/searchresults/searchConstants';

/**
 * Display helpers for alerts. Pure functions over a `t`, shared by the feed
 * dialog, the "Meine Alerts" list and the entry points, so a cadence or a
 * filter reads the same everywhere.
 *
 * Every vocabulary value is the backend's; an unknown one renders raw rather
 * than disappearing.
 */

export function cadenceLabel(cadence: string | null | undefined, t: TFunction): string {
  switch (cadence) {
    case 'immediately':
      return t('alerts.cadence.immediately');
    case 'daily':
      return t('alerts.cadence.daily');
    case 'weekly':
      return t('alerts.cadence.weekly');
    case 'monthly':
      return t('alerts.cadence.monthly');
    default:
      return cadence ?? '—';
  }
}

export function channelLabel(channel: string | null | undefined, t: TFunction): string {
  switch (channel) {
    case 'email':
      return t('alerts.channel.email');
    case 'none':
      return t('alerts.channel.none');
    case 'webhook':
      return t('alerts.channel.webhook');
    default:
      return channel ?? '—';
  }
}

export function feedTypeLabel(feedType: string | null | undefined, t: TFunction): string {
  switch (feedType) {
    case 'insolvencies':
      return t('alerts.feedType.insolvencies');
    case 'new_companies':
      return t('alerts.feedType.new_companies');
    case 'tenders':
      return t('alerts.feedType.tenders');
    case 'saved_search':
      return t('alerts.feedType.saved_search');
    default:
      return feedType ?? '—';
  }
}

export type AlertStatusTone = 'ok' | 'muted' | 'warning' | 'danger';

export function statusInfo(
  status: string | null | undefined,
  t: TFunction,
): { label: string; tone: AlertStatusTone } {
  switch (status) {
    case 'active':
      return { label: t('alerts.status.active'), tone: 'ok' };
    case 'paused':
      return { label: t('alerts.status.paused'), tone: 'muted' };
    case 'paused_credits':
      return { label: t('alerts.status.paused_credits'), tone: 'danger' };
    case 'too_broad':
      return { label: t('alerts.status.too_broad'), tone: 'warning' };
    default:
      return { label: status ?? '—', tone: 'muted' };
  }
}

/** True when the alert is paused for whatever reason (user or credits). */
export function isPaused(sub: AlertSubscription): boolean {
  return (
    sub.paused_at != null ||
    sub.status === 'paused' ||
    sub.status === 'paused_credits'
  );
}

/** Where the thing an alert watches lives in the app. */
export function subjectHref(sub: AlertSubscription): string | null {
  if (sub.subject_type === 'company' && sub.eu_id) {
    return `/watchlist?company=${encodeURIComponent(sub.eu_id)}`;
  }
  if (sub.subject_type === 'list' && sub.list_id != null) {
    return `/my-lists/${sub.list_id}`;
  }
  switch (sub.feed_type) {
    case 'insolvencies':
      return '/insolvenzticker';
    case 'tenders':
      return '/vergabe';
    case 'new_companies':
      return '/neugruendungen';
    case 'saved_search':
      return '/my-lists?tab=searches';
    default:
      return null;
  }
}

export function subjectName(sub: AlertSubscription, t: TFunction): string {
  if (sub.subject_type === 'company') {
    return sub.company_name ?? sub.name ?? sub.eu_id ?? t('alerts.subject.company');
  }
  if (sub.subject_type === 'list') {
    return sub.list_name ?? sub.name ?? t('alerts.subject.list');
  }
  return sub.name ?? feedTypeLabel(sub.feed_type, t);
}

const LEGAL_FORM_LABELS = new Map<string, string>(
  RECHTSFORM_OPTIONS.flatMap((option) =>
    option.codes.map((code) => [code, option.label] as const),
  ),
);

export function legalFormLabel(code: string): string {
  if (code === 'UG') return 'UG (haftungsbeschränkt)';
  return LEGAL_FORM_LABELS.get(code) ?? code;
}

function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function regionLabel(code: string, country: string | null): string {
  if (country === 'CH') return SWISS_CANTON_NAMES[code.toUpperCase()] ?? code;
  return code;
}

function formatRange(
  min: number | null,
  max: number | null,
  locale: string,
): string | null {
  if (min === null && max === null) return null;
  const fmt = new Intl.NumberFormat(locale.startsWith('de') ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency: 'EUR',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  if (min !== null && max !== null) return `${fmt.format(min)} – ${fmt.format(max)}`;
  if (min !== null) return `≥ ${fmt.format(min)}`;
  return `≤ ${fmt.format(max as number)}`;
}

export interface FilterSummaryRow {
  key: string;
  label: string;
  value: string;
}

/**
 * Human-readable rows for a feed's filters, in a stable order. Unknown keys
 * are shown raw so a filter added on the backend is never silently hidden
 * from the user who is about to pay for it.
 */
export function describeFeedFilters(
  feedType: string | null | undefined,
  filters: Record<string, unknown> | null | undefined,
  t: TFunction,
  lang: string,
): FilterSummaryRow[] {
  const f = filters ?? {};
  const rows: FilterSummaryRow[] = [];
  const country = str(f.country);
  const seen = new Set<string>();
  const push = (key: string, label: string, value: string | null) => {
    seen.add(key);
    if (value) rows.push({ key, label, value });
  };

  push('country', t('alerts.filters.country'), country);
  push(
    'regions',
    country === 'CH' ? t('alerts.filters.canton') : t('alerts.filters.region'),
    strings(f.regions).map((r) => regionLabel(r, country)).join(', ') || null,
  );
  push('bundesland', t('alerts.filters.region'), str(f.bundesland));
  push(
    'canton',
    t('alerts.filters.canton'),
    str(f.canton) ? regionLabel(str(f.canton) as string, 'CH') : null,
  );
  push('categories', t('alerts.filters.categories'), strings(f.categories).join(', ') || null);
  push(
    'industries',
    t('alerts.filters.industries'),
    strings(f.industries).map((k) => getCategoryLabel(k, lang)).join(', ') || null,
  );
  push(
    'legal_forms',
    t('alerts.filters.legalForms'),
    strings(f.legal_forms).map(legalFormLabel).join(', ') || null,
  );
  push('keyword', t('alerts.filters.keyword'), str(f.keyword));
  push('cpv_prefixes', t('alerts.filters.cpv'), strings(f.cpv_prefixes).join(', ') || null);
  push(
    'contract_type',
    t('alerts.filters.contractType'),
    str(f.contract_type) ? t(`tenders.${str(f.contract_type)}`) : null,
  );
  push(
    'status',
    t('alerts.filters.status'),
    str(f.status) ? t(`tenders.${str(f.status)}`) : null,
  );
  seen.add('revenue_min');
  push(
    'revenue_max',
    t('alerts.filters.revenue'),
    formatRange(num(f.revenue_min), num(f.revenue_max), lang),
  );
  seen.add('value_min');
  push(
    'value_max',
    t('alerts.filters.value'),
    formatRange(num(f.value_min), num(f.value_max), lang),
  );

  for (const [key, value] of Object.entries(f)) {
    if (seen.has(key) || value == null || value === '') continue;
    const text = Array.isArray(value) ? value.join(', ') : String(value);
    if (text) rows.push({ key, label: key, value: text });
  }

  if (rows.length === 0 && feedType !== 'saved_search') {
    rows.push({
      key: 'none',
      label: t('alerts.filters.scope'),
      value: t('alerts.filters.everything'),
    });
  }
  return rows;
}

/**
 * A name for a new feed, built from its filters: "Insolvenzen · Bayern ·
 * Bau". The user can change it; the backend generates one when none is sent.
 */
export function defaultFeedName(
  feedType: string,
  filters: Record<string, unknown>,
  t: TFunction,
  lang: string,
): string {
  const parts = [feedTypeLabel(feedType, t)];
  const rows = describeFeedFilters(feedType, filters, t, lang).filter(
    (row) => row.key !== 'none' && row.key !== 'country',
  );
  for (const row of rows.slice(0, 2)) parts.push(row.value);
  const name = parts.join(' · ');
  return name.length > 120 ? `${name.slice(0, 117)}…` : name;
}
